/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/suv/suvViewportBridge.js
 *
 * SUV STORAGE — v10.1 (adds partial-key fallback lookup)
 *
 *   data: { "ssUid:roiNum:ctSeriesUid" → { suvMax, suvMean, voxelCount, factors } }
 *
 * v10.1 CHANGE: adds `getSuvByPartialKey(partialKey)` for callers that can't
 * reconstruct the full 3-tuple key — specifically RTStructDisplayTool, whose
 * `contour.structureSetSeriesInstanceUid` is sometimes empty depending on how
 * the toolState was populated. Partial key has the form `:roiNum:ctSeriesUid`
 * (empty first leg). Match policy: find all entries where the suffix matches;
 * return the first if unique, warn if ambiguous.
 *
 * Safety: in single-RTSTRUCT-multi-CT (this codebase's typical setup) and in
 * multi-RTSTRUCT (each ssUid pairs with its own ctUid), ambiguity is
 * structurally impossible — every (roiNum, ctSeriesUid) pair maps to at
 * most one entry. The ambiguity warning would surface bugs where the data
 * model breaks that invariant.
 */
const KEY = '__SUV_BRIDGE_V10__';
function getBridge() {
  if (typeof window === 'undefined') {
    return { data: {}, listeners: new Set(), computingCount: 0 };
  }
  if (!window[KEY]) {
    window[KEY] = { data: {}, listeners: new Set(), computingCount: 0 };
  }
  return window[KEY];
}
function notify() {
  const b = getBridge();
  const ls = Array.from(b.listeners);
  for (const fn of ls) {
    try {
      fn();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SUV-BRIDGE] listener threw', e);
    }
  }
}
/**
 * Compose the canonical bridge key.
 * Pass empty string for any leg you don't have; the bridge will still match.
 */
export function makeSuvKey(structureSetUID, roiNumber, ctSeriesUID) {
  return `${structureSetUID || ''}:${
    roiNumber == null ? '' : roiNumber
  }:${ctSeriesUID || ''}`;
}
export function setSuvByKey(key, value) {
  if (!key) return;
  getBridge().data[key] = value;
  notify();
}
export function getSuvByKey(key) {
  if (!key) return null;
  return getBridge().data[key] || null;
}

/**
 * Partial-key fallback. Use when the caller has roiNumber + ctSeriesUid
 * but not structureSetUID. `partialKey` MUST start with ':' (empty first
 * leg).
 *
 * Searches for entries whose key, with the ssUid leg stripped, equals
 * partialKey. Returns the value of the first match; logs a warning if
 * multiple entries match (suggests a real data-model issue).
 *
 * @param {string} partialKey  e.g. ':21:1.3.6.1.4.1...23643228'
 * @returns {object|null}
 */
export function getSuvByPartialKey(partialKey) {
  if (!partialKey || !partialKey.startsWith(':')) return null;
  const b = getBridge();
  const matches = [];
  for (const k of Object.keys(b.data)) {
    // k = "ssUid:roiNum:ctSeriesUid". Strip everything up to (but not
    // including) the first ':'. Resulting suffix should equal partialKey.
    const firstColon = k.indexOf(':');
    if (firstColon < 0) continue;
    const suffix = k.substring(firstColon); // ":roiNum:ctSeriesUid"
    if (suffix === partialKey) matches.push(k);
  }
  if (matches.length === 0) return null;
  if (matches.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[SUV-BRIDGE] partial-key lookup matched ${matches.length} entries; ` +
        `picking first. partialKey="${partialKey}". keys=[${matches.join(
          ', '
        )}]`
    );
  }
  return b.data[matches[0]] || null;
}

/** Remove entries whose key starts with `prefix` (e.g., a structureSetUID:). */
export function clearSuvByPrefix(prefix) {
  if (!prefix) return;
  const b = getBridge();
  for (const k of Object.keys(b.data)) {
    if (k.startsWith(prefix)) delete b.data[k];
  }
  notify();
}
/** Wipe everything (used on guideline switch off RECIP). */
export function clearAllSuv() {
  const b = getBridge();
  b.data = {};
  notify();
}
export function subscribe(fn) {
  const b = getBridge();
  b.listeners.add(fn);
  return () => b.listeners.delete(fn);
}
export function startComputing() {
  getBridge().computingCount += 1;
  notify();
}
export function stopComputing() {
  const b = getBridge();
  b.computingCount = Math.max(0, b.computingCount - 1);
  notify();
}
export function isComputing() {
  return getBridge().computingCount > 0;
}
/* Devtools helper */
if (typeof window !== 'undefined') {
  window.__SUV_BRIDGE_DUMP__ = () => {
    const b = getBridge();
    // eslint-disable-next-line no-console
    console.log('SUV bridge:', {
      entries: Object.keys(b.data).length,
      computing: b.computingCount,
      listeners: b.listeners.size,
      keys: Object.keys(b.data),
    });
  };
}
